<template>
  <div>
    <div class="field">
      <div class="control">
        <div class="select is-fullwidth">
          <select v-model="selectedOperationId" @change="operationSelected">
            <option v-for="operation in operations" :key="operation.id" :value="operation.id">
              {{ operation.title }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <div class="field">
      <div class="control" v-if="requiresInput">
        <input
          :value="liveCondition.value"
          type="text"
          class="input"
          @input="(e) => textChanged((e.target as HTMLInputElement).value)"
        />
        <p class="help is-danger" v-if="isTextInvalid">Text required</p>
      </div>
    </div>

    <div class="control">
      <n-button variant="primary" @click="complete()">Done</n-button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ConditionViewModel } from '../models/conditionViewModel';
import { OperationPickerOption, Conditions, ConditionOperationType } from '../models/conditions';
import { required, requiredIf } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { PropType, ref, onMounted, computed } from 'vue';
import VuelidateUtil from '@shared/util/vuelidateUtil';

const props = defineProps({
  condition: {
    type: Object as PropType<ConditionViewModel>,
    required: true,
  },
});

const emit = defineEmits<{
  (e: 'changed', condition: ConditionViewModel): void;
  (e: 'completed', condition: ConditionViewModel): void;
}>();

const liveCondition = ref<ConditionViewModel>({
  attribute: '',
  operation: '',
  type: '',
  value: '',
  result: true,
});

const validations = computed(() => ({
  liveCondition: {
    value: {
      required: requiredIf(() => liveCondition.value.operation !== ConditionOperationType.IsSet),
    },
  },
}));

const $v = useVuelidate(validations, { liveCondition });
const isTextInvalid = computed(() => VuelidateUtil.isRequiredInvalid($v, (v) => v.liveCondition.value));

const operations: OperationPickerOption[] = Conditions.Operations.text.map((op, index) => {
  return {
    id: `${index}`,
    title: op.title,
    operation: op.operation,
    result: op.result,
  };
});

const selectedOperationId = ref('');
const requiresInput = computed(() => liveCondition.value.operation !== ConditionOperationType.IsSet);

onMounted(() => {
  if (props.condition) {
    liveCondition.value = {
      attribute: props.condition.attribute,
      operation: props.condition.operation,
      type: props.condition.type,
      value: props.condition.value,
      result: props.condition.result,
      unit: props.condition.unit,
      invalid: props.condition.invalid,
    };

    if (liveCondition.value.invalid) {
      $v.value.$touch();
    }
  }

  selectedOperationId.value = getSelectedOperation();
  operationSelected();
});

function getSelectedOperation() {
  if (liveCondition.value) {
    const selectedOperation = operations.find(
      (op) => op.operation === liveCondition.value.operation && op.result === liveCondition.value.result,
    );
    if (selectedOperation) {
      return selectedOperation.id;
    }
  }

  return operations[0].id;
}

function operationSelected() {
  const selectedOperation = operations.find((op) => op.id === selectedOperationId.value);
  if (selectedOperation) {
    liveCondition.value.operation = selectedOperation.operation;
    liveCondition.value.result = selectedOperation.result;
    emitConditionChanged();
  }
}

function textChanged(value: string) {
  liveCondition.value.value = value;
  $v.value.$touch();
  emitConditionChanged();
}

function emitConditionChanged() {
  if (props.condition.isNew) {
    props.condition.isNew = false;
  } else {
    liveCondition.value.invalid = $v.value.$invalid && requiresInput.value;
    emit('changed', liveCondition.value);
  }
}

function complete() {
  liveCondition.value.invalid = $v.value.$invalid && requiresInput.value;
  emit('completed', liveCondition.value);
}
</script>
